import React, { useState, useMemo, useCallback } from 'react';
import { Check, CheckCircle, CheckSquare, Square, AlertTriangle, Lock, RefreshCw, History, ArrowUpCircle, X } from 'lucide-react';
import { formatCurrency } from '../utils/finance';
import { toLocalDateStr } from '../utils/tracker'; // We need this internal helper now

/**
 * Hook responsible for all local state, rendering helpers, and actions 
 * related to the current Tracker Period and its closure process.
 */
export const useTrackerPeriod = (data, actions, trackerData, trackerOffset, theme) => {

    // --- LOCAL STATE (Extracted from TrackerTab.js) ---
    const [splitOverspend, setSplitOverspend] = useState({ credit: 0, savings: 0, reduce: 0 });
    const [selectedDebtId, setSelectedDebtId] = useState("");
    const [selectedAssetId, setSelectedAssetId] = useState("");
    const [overspendCheckmarks, setOverspendCheckmarks] = useState({ credit: false, savings: false, reduce: false });
    const [splitSurplus, setSplitSurplus] = useState({ asset1: 0, asset2: 0, carryover: 0 });
    const [surplusTarget1, setSurplusTarget1] = useState("");
    const [surplusTarget2, setSurplusTarget2] = useState("");
    const [surplusCheckmarks, setSurplusCheckmarks] = useState({ asset1: false, asset2: false, carryover: false });
    
    // Derived state for credit card list
    const creditCards = useMemo(() => data.debts.filter(d => d.type === 'Credit Card' || d.type === 'credit_card'), [data.debts]);

    // --- ACTION HANDLERS (Extracted and wrapped in useCallback) ---

    const isBillPaid = useCallback((billId) => {
        const key = `${billId}_${toLocalDateStr(trackerData.start)}`;
        return (data.paidBills || []).includes(key);
    }, [data.paidBills, trackerData.start]);
    
    const toggleBillPaid = useCallback((billId) => {
        const key = `${billId}_${toLocalDateStr(trackerData.start)}`;
        const currentPaid = data.paidBills || [];
        if (currentPaid.includes(key)) {
            actions.update('paidBills', currentPaid.filter(k => k !== key));
        } else {
            actions.update('paidBills', [...currentPaid, key]);
        }
    }, [actions, data.paidBills, trackerData.start]);

    const handleCreditSweep = useCallback(() => {
        const confirm = window.confirm("Did you pay your credit card bill? This will mark ALL outstanding credit transactions (past and present) as 'Paid'.");
        if (!confirm) return;

        const newTransactions = data.transactions.map(t => {
            if (t.isCredit && !t.paidOff) return { ...t, paidOff: true };
            return t;
        });
        actions.update('transactions', newTransactions);
    }, [actions, data.transactions]);
    
    const handlePeriodClose = useCallback(() => {
        const remaining = trackerData.remaining;
        
        // 1. EXECUTE THE SPLITS
        if (remaining < 0) {
            if (splitOverspend.reduce > 0) actions.update('pendingBudgetAdjustment', splitOverspend.reduce);
            else actions.update('pendingBudgetAdjustment', 0);

            if (splitOverspend.credit > 0 && selectedDebtId) {
                const debt = data.debts.find(d => d.id === parseInt(selectedDebtId));
                if (debt) actions.updateArrayItem('debts', debt.id, 'balance', debt.balance + splitOverspend.credit);
            }
            if (splitOverspend.savings > 0 && selectedAssetId) {
                const asset = data.assets.find(a => a.id === parseInt(selectedAssetId));
                if (asset) actions.updateArrayItem('assets', asset.id, 'value', Math.max(0, asset.value - splitOverspend.savings));
            }
        } else {
            if (splitSurplus.carryover > 0) actions.update('pendingBudgetAdjustment', -splitSurplus.carryover); 
            else actions.update('pendingBudgetAdjustment', 0);

            if (splitSurplus.asset1 > 0 && surplusTarget1) {
                const asset = data.assets.find(a => a.id === parseInt(surplusTarget1));
                if (asset) actions.updateArrayItem('assets', asset.id, 'value', asset.value + splitSurplus.asset1);
            }
            if (splitSurplus.asset2 > 0 && surplusTarget2) {
                const asset = data.assets.find(a => a.id === parseInt(surplusTarget2));
                if (asset) actions.updateArrayItem('assets', asset.id, 'value', asset.value + splitSurplus.asset2);
            }
        }

        // 2. SAVE HISTORY 
        const breakdown = remaining < 0 ? splitOverspend : splitSurplus;
        
        const result = {
            id: Date.now(),
            start: trackerData.start.toISOString(),
            end: trackerData.end.toISOString(),
            surplus: remaining, 
            action: remaining < 0 ? 'overspend_resolved' : 'surplus_logged',
            breakdown 
        };
        
        const cleanHistory = data.trackerHistory.filter(h => h.end !== result.end);
        actions.update('trackerHistory', [result, ...cleanHistory]);
        
        // 3. ADVANCE NAVIGATION & RESET QUICK ADD
        if (!trackerData.isDirty) {
             setTimeout(() => {
                 actions.setTrackerOffset(trackerOffset + 1); 
                 actions.update('pendingBudgetAdjustment', 0); 
             }, 100);
        }

        // 4. RESET LOCAL STATE
        setSplitOverspend({ credit: 0, savings: 0, reduce: 0 });
        setSplitSurplus({ asset1: 0, asset2: 0, carryover: 0 });
        setSurplusCheckmarks({ asset1: false, asset2: false, carryover: false });
        setSelectedDebtId("");
        setSelectedAssetId("");
        setOverspendCheckmarks({ credit: false, savings: false, reduce: false });
    }, [actions, data.assets, data.debts, data.trackerHistory, trackerData.end, trackerData.remaining, trackerData.start, trackerData.isDirty, trackerOffset, selectedAssetId, selectedDebtId, splitOverspend, splitSurplus]);


    // --- RENDERING HELPERS (Moved from TrackerTab.js) ---

    const renderBalanceScale = useCallback((allocated, target, type) => {
        const percent = Math.min(100, (allocated / target) * 100);
        const isBalanced = Math.abs(allocated - target) < 0.01;
        const isOver = allocated > target + 0.01;
        
        let colorClass = "bg-slate-700";
        if (isOver) colorClass = "bg-red-500 animate-pulse";
        else if (isBalanced) colorClass = "bg-emerald-500";
        else colorClass = type === 'surplus' ? "bg-blue-500" : "bg-orange-500";

        return (
            <div className="w-full mt-4">
                <div className="flex justify-between items-end mb-1">
                    <span className={`text-[10px] font-bold uppercase ${isOver ? 'text-red-400' : 'text-slate-400'}`}>
                        {isBalanced ? "Perfectly Balanced" : isOver ? "Over Allocated!" : "Allocated"}
                    </span>
                    <span className={`text-xs font-mono font-bold ${isBalanced ? 'text-emerald-400' : 'text-white'}`}>
                        {formatCurrency(allocated)} / {formatCurrency(target)}
                    </span>
                </div>
                <div className="h-3 bg-slate-950 rounded-full overflow-hidden border border-slate-800 relative">
                    <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/diagonal-stripes.png')]"></div>
                    <div 
                        className={`h-full transition-all duration-500 ease-out ${colorClass}`} 
                        style={{ width: `${isOver ? 100 : percent}%` }}
                    ></div>
                    {!isBalanced && !isOver && (
                        <div className="absolute top-0 bottom-0 right-0 w-0.5 bg-white/20 animate-pulse"></div>
                    )}
                </div>
                {isOver && <p className="text-[10px] text-red-400 text-right mt-1">Reduce allocation by {formatCurrency(allocated - target)}</p>}
            </div>
        );
    }, []); // No need for external dependencies here, only uses internal logic/state

    const renderHistoricalBreakdown = useCallback(() => {
        const history = trackerData.currentHistoryItem;
        if (!history || !history.breakdown) return <p className="text-xs text-slate-500 italic">No detailed breakdown available for this period.</p>;

        const bd = history.breakdown;
        const items = [];

        // Parsing Overspend Breakdown
        if (bd.credit > 0) items.push({ label: "Added to Credit Balance", amount: bd.credit, color: "text-orange-400" });
        if (bd.savings > 0) items.push({ label: "Pulled from Savings", amount: bd.savings, color: "text-blue-400" });
        if (bd.reduce > 0) items.push({ label: "Reduced Future Budget", amount: bd.reduce, color: "text-red-400" });

        // Parsing Surplus Breakdown
        if (bd.asset1 > 0) items.push({ label: "Allocated to Asset", amount: bd.asset1, color: "text-emerald-400" });
        if (bd.asset2 > 0) items.push({ label: "Allocated to Asset", amount: bd.asset2, color: "text-emerald-400" });
        if (bd.carryover > 0) items.push({ label: "Added to Future Budget", amount: bd.carryover, color: "text-emerald-400" });

        if (items.length === 0) return <p className="text-xs text-slate-500">Period closed with no allocation necessary.</p>;

        return (
            <div className="space-y-2 mt-2 bg-slate-950 p-3 rounded border border-slate-800">
                {items.map((item, i) => (
                    <div key={i} className="flex justify-between text-xs">
                        <span className="text-slate-400">{item.label}</span>
                        <span className={`font-bold font-mono ${item.color}`}>{item.amount > 0 ? formatCurrency(item.amount) : formatCurrency(item.amount)}</span>
                    </div>
                ))}
            </div>
        );
    }, [trackerData.currentHistoryItem]);


    return {
        // State variables
        splitOverspend, setSplitOverspend,
        selectedDebtId, setSelectedDebtId,
        selectedAssetId, setSelectedAssetId,
        overspendCheckmarks, setOverspendCheckmarks,
        splitSurplus, setSplitSurplus,
        surplusTarget1, setSurplusTarget1,
        surplusTarget2, setSurplusTarget2,
        surplusCheckmarks, setSurplusCheckmarks,
        creditCards,
        
        // Handlers
        handlePeriodClose,
        isBillPaid, toggleBillPaid,
        handleCreditSweep,
        
        // Renderers
        renderBalanceScale,
        renderHistoricalBreakdown,
    };
};